<template>
  <div
    class="sc-form-input relative sc-datepicker-input"
    :class="[marginClasses, sizeClasses, errorClass]"
    :ref="`${computedName}-sc-datepicker`"
  >
    <div class="flex">
      <div class="flex items-center w-full">
        <div class="w-full">
          <input
            :id="id"
            :ref="id"
            :name="name"
            :aria-label="label"
            v-model="showValue"
            class="sc-purchase-input"
            :class="{ active: show }"
            :type="type"
            :placeholder="placeholder"
            :readonly="readonly"
            :disabled="disabled"
            @keydown.delete="deleteEmpty()"
            @keydown.enter="$emit('enter')"
            @focus="showCalendar"
            autocomplete="off"
          />
          <transition
            enter-active-class="fadeIn"
            leave-active-class="fadeOut"
            mode="out-in"
          >
            <div v-if="errorClass.length" class="input-error">
              <div class="has-tooltip">
                <img
                  :src="`${scBaseUrl}/assets/images/Error.svg`"
                  alt="Error"
                />
                <BaseToolTip
                  :ref="`tooltip-error-${id}`"
                  v-if="errorMessage"
                  class="tooltip-input"
                  tip="bottom-left"
                >
                  {{ errorMessage }}
                </BaseToolTip>
              </div>
            </div>
          </transition>
          <transition
            enter-active-class="fadeIn"
            leave-active-class="fadeOut"
            mode="out-in"
          >
            <div v-if="loading" class="input-loader">
              <img
                :src="`${scBaseUrl}/assets/images/icons/preloadder.gif`"
                alt="Loading..."
              />
            </div>
          </transition>
          <transition
            enter-active-class="fadeIn"
            leave-active-class="fadeOut"
            mode="out-in"
          >
            <div v-if="success" class="input-checked">
              <div class="has-tooltip">
                <img
                  :src="`${scBaseUrl}/assets/images/Checked.svg`"
                  alt="Checked"
                />
                <BaseToolTip
                  class="tooltip-input"
                  :ref="`tooltip-success-${id}`"
                  tip="bottom-left"
                  v-if="successMessage"
                  >{{ successMessage }}</BaseToolTip
                >
              </div>
            </div>
          </transition>
        </div>
      </div>
      <slot></slot>
    </div>
    <transition
      enter-active-class="fadeInLeft"
      leave-active-class="fadeOut"
      mode="out-in"
    >
      <div
        v-if="show"
        ref="picker"
        class="sc-datepicker-box"
        v-closable="{
          exclude: [`${computedName}-sc-datepicker`],
          handler: 'hideCalendar',
        }"
        :style="`top:-${topPosition}px;z-index:${(
          highestZIndex + 117
        ).toString()};`"
      >
        <BaseFormRow>
          <BaseLabel
            v-if="label && showLabel"
            :size="size"
            :for="id"
            :required="required"
            >{{ label }}</BaseLabel
          >
          <!-- <select class="sc-yearselect" :id="`${id}-year`" v-model="selectedYear">
            <option v-for="year in yearsList" :value="year">{{ year }}</option>
          </select>-->
          <BaseYearSelect
            id="dob_year"
            v-model="selectedYear"
            placeholder="Year"
            :options="yearsList"
            class="sc-yearselect"
          />
        </BaseFormRow>
        <BaseFormRow class="sc-months">
          <a
            class="nav"
            href="javascript:"
            :class="{
              disabled: isDisabledMonth(
                selectedMonth == 0 ? 11 : selectedMonth - 1
              ),
            }"
            @click="
              isDisabledMonth(selectedMonth == 0 ? 11 : selectedMonth - 1)
                ? ''
                : selectedMonth--
            "
          >
            <img :src="`${scBaseUrl}/assets/images/Arrow-left.svg`" alt="Pre" />
          </a>
          <template v-for="(month, index) in activeMonthList">
            <a
              :key="`month-${index}`"
              href="javascript:"
              :class="{
                active: month == selectedMonth,
                disabled: isDisabledMonth(month),
              }"
              @click="
                isDisabledMonth(month) || index == 1
                  ? ''
                  : index > 1
                  ? (selectedMonth += index - 1)
                  : selectedMonth--
              "
              >{{ monthList[month].shortText }}</a
            >
          </template>
          <a
            class="nav"
            href="javascript:"
            :class="{
              disabled: isDisabledMonth(
                selectedMonth == 11 ? 1 : selectedMonth + 1
              ),
            }"
            @click="
              isDisabledMonth(selectedMonth == 11 ? 1 : selectedMonth + 1)
                ? ''
                : selectedMonth++
            "
          >
            <img
              :src="`${scBaseUrl}/assets/images/Arrow-right.svg`"
              alt="Next"
            />
          </a>
        </BaseFormRow>
        <BaseFormRow class="sc-weekdays">
          <a href="javascript:" v-for="(day, index) in daysList">{{
            day.singleText
          }}</a>
        </BaseFormRow>
        <ul v-if="selectedDate" class="sc-datelist">
          <template v-for="date in activeDaysList">
            <li
              :key="`date${date.scFormat()}`"
              @click="selectDate(date)"
              :class="{
                active: date.scFormat() == selectedDate.scFormat(),
                disabled:
                  date > maxDate ||
                  date < minDate ||
                  date.getMonth() != selectedMonth,
              }"
            >
              {{ pad(date.getDate(), 2) }}
            </li>
          </template>
        </ul>
        <BaseFormRow class="sc-actions">
          <a href="javascript:" @click="hideCalendar">Cancel</a>
          <button type="button" @click="setDate">OK</button>
        </BaseFormRow>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  props: {
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number],
      default: null,
    },
    label: {
      type: String,
      required: false,
    },
    showLabel: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: "text",
    },
    placeholder: {
      type: String,
      default: "",
    },
    required: {
      type: Boolean,
      default: false,
    },
    error: {
      type: Boolean,
      default: false,
    },
    errorMessage: {
      type: String,
      default: "",
    },
    successMessage: {
      type: String,
      default: "",
    },
    success: {
      type: Boolean,
      default: false,
    },
    readonly: {
      default: false,
      type: Boolean,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    size: {
      default: "w-full",
      type: String,
    },
    className: {
      default: "",
      type: String,
    },
    margin: {
      default: "base",
      type: String,
    },
    disabled: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      show: false,
      selectedDate: "",
      selectedByUser: false,
      showValue: "",
      selectedYear: 1980,
      selectedMonth: new Date().getMonth(),
      minDate: "",
      maxDate: "",
      monthList: [
        {
          fullText: "January",
          shortText: "Jan",
          numericString: "01",
        },
        {
          fullText: "Febuary",
          shortText: "Feb",
          numericString: "02",
        },
        {
          fullText: "March",
          shortText: "Mar",
          numericString: "03",
        },
        {
          fullText: "April",
          shortText: "Apr",
          numericString: "04",
        },
        {
          fullText: "May",
          shortText: "May",
          numericString: "05",
        },
        {
          fullText: "June",
          shortText: "Jun",
          numericString: "06",
        },
        {
          fullText: "July",
          shortText: "Jul",
          numericString: "07",
        },
        {
          fullText: "August",
          shortText: "Aug",
          numericString: "08",
        },
        {
          fullText: "September",
          shortText: "Sep",
          numericString: "09",
        },
        {
          fullText: "October",
          shortText: "Oct",
          numericString: "10",
        },
        {
          fullText: "November",
          shortText: "Nov",
          numericString: "11",
        },
        {
          fullText: "December",
          shortText: "Dec",
          numericString: "12",
        },
      ],
      daysList: [
        {
          fullText: "Sunday",
          shortText: "Sun",
          singleText: "S",
          numericString: "01",
        },
        {
          fullText: "Monday",
          shortText: "Mon",
          singleText: "M",
          numericString: "02",
        },
        {
          fullText: "Tuesday",
          shortText: "Tue",
          singleText: "T",
          numericString: "03",
        },
        {
          fullText: "Wednesday",
          shortText: "Wed",
          singleText: "W",
          numericString: "04",
        },
        {
          fullText: "Thursday",
          shortText: "Thu",
          singleText: "T",
          numericString: "05",
        },
        {
          fullText: "Friday",
          shortText: "Fri",
          singleText: "F",
          numericString: "06",
        },
        {
          fullText: "Saturday",
          shortText: "Sat",
          singleText: "S",
          numericString: "07",
        },
      ],
    };
  },
  created() {
    this.maxDate = new Date(
      new Date().getFullYear() - 16,
      this.selectedMonth,
      new Date().getDate()
    );
    this.minDate = new Date(
      this.maxDate.getFullYear() - 100,
      this.selectedMonth,
      new Date().getDate()
    );
  },
  watch: {
    selectedMonth() {
      if (this.selectedMonth > 11) {
        if (this.maxYear == this.selectedYear) {
          this.selectedMonth = 11;
        } else {
          this.selectedMonth = 0;
          this.selectedYear++;
        }
      }
      if (this.selectedMonth < 0) {
        if (this.minYear == this.selectedYear) {
          this.selectedMonth = 0;
        } else {
          this.selectedMonth = 11;
          this.selectedYear--;
        }
      }
    },
    selectedYear() {
      if (
        this.selectedYear == this.maxYear &&
        this.selectedMonth > this.maxDate.getMonth()
      ) {
        this.selectedMonth = this.maxDate.getMonth();
      } else if (
        this.selectedYear == this.minYear &&
        this.selectedMonth < this.minDate.getMonth()
      ) {
        this.selectedMonth = this.minDate.getMonth();
      }
    },
    selectedDate() {
      if (!this.selectedDate) {
        this.showValue = "";
      } else {
        this.showValue = this.selectedDate.scFormatShort();
      }
    },
    showValue() {
      if (this.showValue.length) {
        let value = this.showValue;
        value = value.replace(/\D/g, "");
        value = value.match(/.{1,2}/g).join("/");
        this.showValue = value;
        // if (value.length < 2) {
        //   value = this.pad(value, 2);
        // }

        // if (value.length < 4) {
        //   value += this.pad(this.selectedMonth + 1, 2);
        // }
        // if (value.length < 5) {
        //   value += this.selectedYear.toString().slice(-2);
        // }
        let stringArray = value.split("/");
        while (stringArray.length > 3) {
          stringArray.pop();
        }

        if (stringArray.length == 3 && stringArray.join("").length > 5) {
          if (stringArray[0]) {
            if (stringArray[0] > 31) {
              stringArray[0] = "01";
              this.showValue = stringArray.join("/");
            }
          }
          if (stringArray[1]) {
            if (stringArray[1].length > 2) {
              stringArray[1] = this.pad(
                stringArray[1].slice(2 - stringArray[1].length),
                2
              );
              this.showValue = stringArray.join("/");
            }
            if (parseInt(stringArray[1]) > 12) {
              stringArray[1] = 12;
              this.showValue = stringArray.join("/");
            }
            if (stringArray[1].length > 1 && parseInt(stringArray[1]) < 1) {
              stringArray[1] = "01";
              this.showValue = stringArray.join("/");
            }
          }
          if (stringArray[2]) {
            if (parseInt(stringArray[2]) > 99) {
              stringArray[2] = 99;
              this.showValue = stringArray.join("/");
            }
          }
          var year = stringArray.splice(-1);
          let year = this.yearsList.find(
            (yr) => year == yr.toString().slice(-2)
          );
          if (!year) {
            stringArray[2] = this.yearsList[this.yearsList.length - 1].splice(
              -2
            );
            this.showValue = stringArray.join("/");
          }
          if (year) {
            var date = new Date(
              year,
              parseInt(stringArray[1]) - 1,
              stringArray[0]
            );
            this.selectedDate = date;
            this.selectedMonth = date.getMonth();
            this.selectedYear = date.getFullYear();
          }
        }
      } else {
        this.selectedByUser = false;
      }
    },
    show(show) {
      let scForm = document.getElementsByClassName("sc-form")[0];
      if (!show) {
        scForm.style.margin = "96px auto 27px auto";
        return;
      }
      setTimeout(() => {
        let calenderInput = document.getElementsByClassName(
          "sc-datepicker-input"
        )[0];
        if (calenderInput.offsetTop + 100 < 425) {
          scForm.style.margin =
            430 - calenderInput.offsetTop + "px auto 27px auto";
        }
      }, 100);
    },
  },
  computed: {
    maxYear() {
      return this.maxDate.getFullYear();
    },
    minYear() {
      return this.minDate.getFullYear();
    },
    activeMonthList() {
      let activeMonths = [this.selectedMonth];

      if (this.selectedMonth == 0) {
        activeMonths.unshift(11);
      } else {
        activeMonths.unshift(this.selectedMonth - 1);
      }

      activeMonths.push(this.selectedMonth + 1);
      activeMonths.push(this.selectedMonth + 2);
      activeMonths.push(this.selectedMonth + 3);
      for (const month in activeMonths) {
        if (activeMonths[month] < 0) {
          activeMonths[month] = 12 - activeMonths[month];
        }
        if (activeMonths[month] > 11) {
          activeMonths[month] = activeMonths[month] - 12;
        }
      }
      return activeMonths;
    },
    activeDaysList() {
      let activeDays = [];
      let firstDateOfMonth = new Date(
        `${this.selectedYear}-${
          this.monthList[this.selectedMonth].numericString
        }-01`
      );
      let dayOfStart = firstDateOfMonth.getDay();
      let lastDayOfMonth = new Date(
        this.selectedYear,
        this.selectedMonth + 1,
        0
      );
      let dayOfEnd = lastDayOfMonth.getDay();
      for (let index = 0; index < lastDayOfMonth.getDate(); index++) {
        activeDays.push(
          new Date(
            `${this.selectedYear}-${
              this.monthList[this.selectedMonth].numericString
            }-${index < 9 ? "0" + (index + 1) : index + 1}`
          )
        );
      }
      for (let index = 0; index < dayOfStart; index++) {
        var date = new Date(activeDays[0]);
        date.setDate(date.getDate() - 1);

        activeDays.unshift(date);
        // activeDays.unshift(
        //   `${date.getFullYear()}-${
        //     date.getMonth() > 9
        //       ? date.getMonth() + 1
        //       : "0" + (date.getMonth() + 1)
        //   }-${date.getDate() < 9 ? "0" + date.getDate() : date.getDate()}`
        // );
      }
      for (let index = 0; index < 6 - dayOfEnd; index++) {
        var date = new Date(activeDays[activeDays.length - 1]);
        date.setDate(date.getDate() + 1);
        activeDays.push(date);
        // activeDays.push(
        //   `${date.getFullYear()}-${
        //     date.getMonth() > 9
        //       ? date.getMonth() + 1
        //       : "0" + (date.getMonth() + 1)
        //   }-${date.getDate() < 9 ? "0" + date.getDate() : date.getDate()}`
        // );
      }
      return activeDays;
    },
    yearsList() {
      let years = [];
      let max_year = new Date().getFullYear() - 16;
      for (let index = max_year - 100; index < max_year + 1; index++) {
        years.push(index);
      }
      return years;
    },
    sizeClasses() {
      return this.className || this.size;
    },
    marginClasses() {
      return this.margin;
    },
    computedName() {
      if (this.name) {
        return this.name;
      } else if (this.id) {
        return this.id;
      } else {
        return "";
      }
    },
    errorClass() {
      if (this.error && !this.value.length) {
        return "has-error";
      } else {
        return "";
      }
    },
    topPosition() {
      if (this.activeDaysList.length > 35) {
        // return 430;
      }
      return 430;
    },
  },
  methods: {
    deleteEmpty() {
      if (!this.value) {
        this.$emit("remove-empty");
      }
    },
    showCalendar() {
      if (!this.selectedDate) {
        this.selectedDate = new Date(
          this.selectedYear,
          this.selectedMonth,
          new Date().getDate()
        );
        this.$emit("input", this.selectedDate.scFormat());
        this.setDate();
      }
      this.show = true;
    },
    hideCalendar() {
      if (!this.selectedByUser) {
        this.selectedDate = "";
        this.$emit("input","");
        this.$emit("remove-empty");
      }
      this.show = false;
    },
    pad(str, max) {
      str = str.toString();
      return str.length < max ? this.pad("0" + str, max) : str;
    },
    isDisabledMonth(month) {
      var year = this.selectedYear;
      if (!this.monthList[month]) {
        month = this.monthList.length - 1;
        year--;
      }
      if (
        this.activeMonthList.indexOf(month) == 0 &&
        this.activeMonthList[1] < month
      ) {
        year--;
      }
      if (
        this.activeMonthList.indexOf(month) > 1 &&
        this.activeMonthList[1] > month
      ) {
        year++;
      }
      let firstDateOfMonth = new Date(
        `${year}-${this.monthList[month].numericString}-01`
      );
      let lastDayOfMonth = new Date(year, month + 1, 0);
      if (year > this.maxYear - 1 && this.maxDate < firstDateOfMonth) {
        return true;
      }
      if (year < this.minYear + 1 && this.minDate > lastDayOfMonth) {
        return true;
      }
      return false;
    },
    selectDate(date) {
      if (date > this.maxDate || date < this.minDate) {
        return false;
      }
      this.selectedByUser = true;
      this.selectedDate = date;
      this.selectedMonth = date.getMonth();
      this.selectedYear = date.getFullYear();
    },
    setDate() {
      this.selectedByUser = true;
      this.$emit("input", this.selectedDate.scFormat());
      this.hideCalendar();
    },
  },
};
</script>
<style scoped>
.sc-datepicker-box .sc-select-input.sc-year {
  border: none !important;
}

.sc-datepicker-box {
  position: absolute;
  left: -10px;
  background: #ffffff;
  box-shadow: 0 15px 50px 0 rgba(54, 58, 64, 0.15);
  border-radius: 8px 8px 8px 0;
  width: 220px;
  min-height: 385px;
  padding: 20px 35px;
}

.sc-datepicker-box:before {
  content: "";
  background: url(https://sellcodes.com/assets/images/left-curve.png) right
    bottom no-repeat;
  background-size: 100%;
  width: 16px;
  height: 13px;
  position: absolute;
  left: 0;
  bottom: -12px;
}

.sc-datepicker-box .sc-months a {
  cursor: pointer;
  text-align: center;
  font-size: 10px;
  font-family: latoblack;
  margin: auto;
  color: #97a1b1;
  line-height: 30px;
  width: 40px;
  height: 30px;
}
.sc-datepicker-box .sc-months a.show {
  display: block;
}
.sc-datepicker-box .sc-months a.nav {
  display: block;
  width: 10px;
}

.sc-datepicker-box .sc-months a.active {
  background: #ffd96b;
  border-radius: 5px;
  border-radius: 5px;
  color: #363a40;
}

.sc-datepicker-box .sc-yearselect {
  font-family: latoregular;
  font-size: 15px;
  color: #97a1b1;
  letter-spacing: 0;
  height: 40px;
  width: 100px;
  line-height: 40px;
}

.sc-datepicker-box .sc-row label {
  font-family: latoblack;
  color: #363a40;
  font-size: 15px;
  line-height: 18px;
  padding: 10px 0;
  width: 100px;
}

.sc-datepicker-box .sc-weekdays {
  margin-bottom: 0;
}

.sc-datepicker-box .sc-weekdays a {
  font-family: latoregular;
  font-size: 10px;
  color: #97a1b1;
  letter-spacing: 0;
  text-align: center;
  width: 30px;
  height: 30px;
  line-height: 30px;
}

.sc-datepicker-box .disabled {
  opacity: 0.5;
}

.sc-datepicker-box .sc-datelist {
  word-wrap: break-word;
  margin: 0;
  padding: 0;
  width: 100%;
  display: block;
  min-height: 198px;
}

.sc-datepicker-box .sc-datelist li {
  font-family: latoblack;
  font-size: 10px;
  color: #97a1b1;
  letter-spacing: 0;
  text-align: center;
  cursor: pointer;
  height: 30px;
  width: 30px;
  line-height: 30px;
  display: inline-block;
  margin: 0 0.7px;
}

.sc-datepicker-box .sc-datelist li.active {
  background: #eaedf1;
  border-radius: 50%;
}

.sc-datepicker-box .sc-actions {
  margin: 15px 0 0;
}

.sc-datepicker-box .sc-actions a {
  font-family: latoregular;
  font-size: 13px;
  color: #97a1b1;
  letter-spacing: 0;
  text-align: center;
  line-height: 40px;
  text-decoration: underline;
}

.sc-datepicker-box .sc-actions button {
  background: #d5dae1;
  border-radius: 8px;
  border-radius: 8px;
  font-family: latoblack;
  font-size: 13px;
  color: #ffffff;
  letter-spacing: 0;
  text-align: center;
  line-height: 40px;
  width: 53px;
  height: 40px;
}
</style>



// WEBPACK FOOTER //
// BaseDatePicker.vue?42275d3c